import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getDashboard } from '../api/dashboard';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import type { DashboardData } from '../types/dashboard';

interface DashboardContextValue {
  data: DashboardData | null;
  /** True only when there is no cached dashboard to show yet. */
  loading: boolean;
  /** Background refresh — keep showing `data`. */
  refreshing: boolean;
  refresh: (options?: { silent?: boolean }) => Promise<void>;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { toast } = useNotification();
  const location = useLocation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const requestId = useRef(0);
  const onDashboard = location.pathname === '/';

  const refresh = useCallback(
    async (options?: { silent?: boolean }) => {
      const reqId = ++requestId.current;
      const silent = options?.silent ?? data != null;
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const next = await getDashboard();
        if (requestId.current === reqId) {
          setData(next);
        }
      } catch (err) {
        if (requestId.current === reqId && !silent) {
          console.error('Dashboard load failed:', err);
          toast.error('Failed to load dashboard');
        }
      } finally {
        if (requestId.current === reqId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [data, toast],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setData(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (onDashboard) {
      void refresh({ silent: data != null });
    }
  }, [isAuthenticated, onDashboard]); // eslint-disable-line react-hooks/exhaustive-deps -- refresh when route/auth changes; not on every data tick

  useEffect(() => {
    if (!isAuthenticated) return;
    const onVis = () => {
      if (document.visibilityState === 'visible' && (onDashboard || data != null)) {
        void refresh({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated, onDashboard, data, refresh]);

  return (
    <DashboardContext.Provider value={{ data, loading, refreshing, refresh }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard(): DashboardContextValue {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}
